import { Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { CustomDataRetriever } from './custom-data/custom-data-retriever.abstract';
import { CustomDataRetrieversServicesMap } from './custom-data/custom-data.const';
import { DynamicLocaleService } from './dynamic-locale.service';
import { GroupBase } from './models/blueprint.model';
import { DataObject, ReportRecord } from './models/report.model';
import { TemplateField } from './models/template.model';
import { ReportFieldFormatterService } from './report-field-formatter.service';

@Injectable()
export class InterpolationFillerService {
  constructor(
    private reportFieldFormatterService: ReportFieldFormatterService,
    private injector: Injector,
    private dynamicLocaleService: DynamicLocaleService
  ) {}

  fillInterpolation(
    group: GroupBase<TemplateField>,
    record: ReportRecord,
    settings: DataObject,
    templateId: number,
    stringInjectorMap: { [key: string]: string | Function },
    reportId?: number
  ) {
    // generate header label
    if (group.header && group.header.label) {
      group.header.label = this.parseFieldLabel(group.header.label, stringInjectorMap);
    }
    const { reportOptions = {} } = settings || {};
    // obtain visible only fields
    const visibleFieldsOnly = group.fields.filter(field => {
      return this.reportFieldFormatterService.isFieldVisible(record, {
        templateId,
        field,
        reportOptions,
      });
    });
    const fields = visibleFieldsOnly.map(field => {
      const { id, options, value: fieldValue, customDataService } = field;

      let value: Observable<string | string[]> = null;
      if (customDataService == null) {
        if (fieldValue == null) {
          value = this.getGroupValue(field, record, settings, templateId, reportId);
        } else {
          value = of(fieldValue);
        }
      } else {
        value = this.getFieldCustomData(reportId, field, templateId, record);
      }

      const labelSettings = { templateId, field };
      const label = this.reportFieldFormatterService.getLabelDesc(record, labelSettings);
      const withOptions = options != null ? { options } : {};
      return { id, label, value, ...withOptions };
    });
    return { ...(group as GroupBase<TemplateField>), fields };
  }

  private getGroupValue(
    field: TemplateField,
    record: ReportRecord,
    settings: DataObject,
    templateId: number,
    reportId?: number
  ): Observable<string | string[]> {
    const { fieldLevelJoinString } = field;
    const {
      joinString = false,
      onlyUrl = true,
      symbols = null,
      reportOptions = {},
      recordsLength = 0,
    } = settings || {};
    const fieldSettings = {
      templateId,
      field,
      joinString: fieldLevelJoinString == null ? joinString : fieldLevelJoinString,
      onlyUrl,
      symbols,
      reportId,
      reportOptions,
      recordsLength,
    };
    return this.reportFieldFormatterService.getAndFormatField(record, fieldSettings) as Observable<string | string[]>;
  }

  private parseFieldLabel(label: string, stringInjectorMap?: { [key: string]: string | Function }) {
    if (stringInjectorMap != null && label != null && !Array.isArray(label)) {
      return label.replace(/\${([^}]+)}/g, (_, prop) => {
        const injector = stringInjectorMap[prop];
        let value = injector.toString();
        if (typeof injector === 'function') {
          value = injector();
        }
        return value;
      });
    }
    return label;
  }

  private getFieldCustomData(reportId: number, field: TemplateField, templateId: number, record: ReportRecord) {
    const { customDataService, customDataPayload } = field;
    const injectionToken = CustomDataRetrieversServicesMap[customDataService];
    const service = this.injector.get<CustomDataRetriever>(injectionToken);
    const localeId = this.dynamicLocaleService.getReportLocaleId();

    return service.getCustomData(reportId, templateId, localeId, customDataPayload, record);
  }
}
